/**
 * The stateless-completions transport — asab-gateway's OpenAI-compatible API.
 *
 * Named for what it wraps, not for who consumes it: any surface wanting a
 * model-picked, stateless turn uses this.
 *
 * `/v1/chat/completions` keeps no session, so every turn re-sends the
 * conversation. That is the fundamental difference from the A2A transport, which
 * addresses a server-side session by `contextId` and would duplicate the
 * conversation if it did the same.
 *
 * `respondToApproval` and `submitToolResult` are absent rather than throwing
 * stubs: the capability flags say this transport cannot pause for a human, and
 * the type should agree with them.
 */
import OpenAI from 'openai';
import type { ChatCompletionMessageParam } from 'openai/resources/chat/completions';

import type { ChatEvent } from '../types/events';
import type { ChatMessage, ContentPart } from '../types/message';
import type { ChatCapabilities, ChatInput, ChatTransport, ContentKind } from '../types/transport';
import { createOpenAIChunkMapper, type OpenAIChunk } from './mapping/openaiChunk';
import { toUserMessage } from '@/lib/api/errors';

const ALL_KINDS: ContentKind[] = ['text', 'image', 'audio'];

export interface OpenAIAuthConfig {
  token: string;
  isApiKey: boolean;
}

export interface OpenAITransportOptions {
  authConfig: OpenAIAuthConfig;
  baseURL?: string;
  /** Content kinds this transport accepts. Defaults to all. */
  accept?: ContentKind[];
  /** Per-turn completion parameters (temperature, tools, response_format, …). */
  params?: Record<string, unknown>;
}

/** ContentPart[] → OpenAI content. Plain text collapses to a bare string, which
 *  every OpenAI-compatible provider accepts; the array form is not universal. */
function toOpenAIContent(content: ContentPart[]): string | Array<Record<string, unknown>> {
  if (content.length === 1 && content[0].type === 'text') return content[0].text;

  return content.map((part) => {
    switch (part.type) {
      case 'text':
        return { type: 'text', text: part.text };
      case 'image':
        return {
          type: 'image_url',
          image_url: { url: `data:${part.mimeType};base64,${part.data}` },
        };
      case 'audio':
        return {
          type: 'input_audio',
          input_audio: { data: part.data, format: part.mimeType.split('/')[1] ?? 'wav' },
        };
    }
  });
}

function toOpenAIMessages(history: ChatMessage[], current: ContentPart[]): ChatCompletionMessageParam[] {
  const messages = history
    // Only settled turns are context. A failed or cancelled turn has no content
    // worth replaying, and a still-streaming one is this very request.
    .filter((m) => m.status === 'complete')
    .map(
      (m) =>
        ({ role: m.role, content: toOpenAIContent(m.content) }) as ChatCompletionMessageParam,
    );
  messages.push({ role: 'user', content: toOpenAIContent(current) } as ChatCompletionMessageParam);
  return messages;
}

export function createOpenAITransport(opts: OpenAITransportOptions): ChatTransport {
  const baseURL = opts.baseURL ?? import.meta.env.VITE_CHAT_API_BASE_URL;
  if (!baseURL) throw new Error('VITE_CHAT_API_BASE_URL is not configured');

  const accept = opts.accept ?? ALL_KINDS;
  const capabilities: ChatCapabilities = {
    // The completions API returns an answer, not a reasoning trace.
    steps: false,
    approvals: false,
    // Text-only means no attach control at all, rather than one that rejects
    // everything it is given.
    multimodalInput: accept.some((k) => k !== 'text'),
    cancel: true,
    modelPicker: true,
    accept,
  };

  const client = new OpenAI({
    apiKey: opts.authConfig.token,
    baseURL,
    dangerouslyAllowBrowser: true,
  });

  return {
    capabilities,

    async *send(input: ChatInput, signal: AbortSignal): AsyncIterable<ChatEvent> {
      const runId = `run-${Date.now()}`;
      const mapper = createOpenAIChunkMapper({
        threadId: input.conversationId ?? runId,
        runId,
      });

      let stream: AsyncIterable<OpenAIChunk>;
      try {
        stream = (await client.chat.completions.create(
          {
            ...opts.params,
            model: input.model ?? '',
            messages: toOpenAIMessages(input.history ?? [], input.content),
            stream: true,
            // Ask for the usage chunk, so cost attribution does not require a
            // second round-trip.
            stream_options: { include_usage: true },
          },
          { signal },
        )) as unknown as AsyncIterable<OpenAIChunk>;
      } catch (err) {
        if (signal.aborted) return;
        yield* mapper.error(toUserMessage(err, 'The model could not be reached. Try again.'));
        return;
      }

      try {
        for await (const chunk of stream) {
          if (signal.aborted) return;
          yield* mapper.map(chunk);
        }
      } catch (err) {
        // An abort surfaces here as a throw; it is a user action, not a failure.
        if (signal.aborted) return;
        yield* mapper.error(toUserMessage(err, 'The response was interrupted. Try again.'));
        return;
      }

      // Close out a stream that ended without a finish_reason.
      yield* mapper.finish();
    },

    async cancel() {
      // Nothing server-side to cancel: aborting the request is the cancellation,
      // and `send`'s AbortSignal already does it.
    },
  };
}
